import Dinero from 'dinero.js'
import { v4 as uuidv4 } from 'uuid'
import {
  getNetValue,
  getTotalPriceDeduction,
  getTotalWeightDeduction,
  generateAdjustmentText,
} from 'utils/data-manipulation/common'
import { formatNumber, setDecimalPrecision, convertUM } from 'utils/helpers'
import { MEASURE_UNIT, ROUNDING_PRECISIONS } from 'utils/constants'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { keyBy } from 'lodash'

const findOverrideNameFromId = (materialTypeId, orderEntryId, priceEntries) => {
  const priceEntry = (priceEntries || []).find(
    entry => entry?.materialTypeId === materialTypeId && (orderEntryId ? entry?.id === orderEntryId : true),
  )
  return priceEntry?.overrideName || priceEntry?.materialName || ''
}

const formatMaterials = (tickets, defaultPriceSheet, priceSheet, priceEntriesByPurchaseOrderId) => {
  const formattedMaterials = {}

  const addFinishedGoodToDict = (finishedGood, key, ticketId) => {
    const { materialType, materialTypeId, orderEntryId, weight } = finishedGood

    if (!formattedMaterials[key]) {
      const materialName =
        findOverrideNameFromId(materialTypeId, orderEntryId, priceSheet?.entriesObj) ||
        materialType?.commonName ||
        (defaultPriceSheet?.entriesObj || []).find(entry => entry.materialTypeId === materialTypeId)?.materialName ||
        ''

      formattedMaterials[key] = {
        ticketId,
        materialTypeId,
        orderEntryId,
        materialName,
        gross: 0,
        tare: 0,
        deductions: [],
        net: 0,
        totalValue: 0,
      }
    }
    formattedMaterials[key].gross += weight.gross
    formattedMaterials[key].tare += weight.tare
    formattedMaterials[key].net += weight.net
  }

  tickets.forEach(ticket => {
    const { id: ticketId, finishedGoods = [], packingLists = [], materials = [], materialWeights = [] } = ticket

    finishedGoods.forEach(finishedGood => {
      addFinishedGoodToDict(finishedGood, finishedGood.id, ticketId)
    })
    packingLists.forEach(packingList => {
      packingList.finishedGoods.forEach(finishedGood => {
        addFinishedGoodToDict(finishedGood, packingList.id, ticketId)
      })
    })
    // only formats materials for IBTs (OBTs do not have weightAndPrice)
    materials.forEach(material => {
      const { id, weightAndPrice, materialType, materialTypeId, orderEntryId } = material
      const poId = ticket.transportationInfo?.purchaseOrderId
      const priceEntries = priceEntriesByPurchaseOrderId?.[poId] || priceSheet?.entriesObj || []
      if (weightAndPrice) {
        formattedMaterials[id] = {
          ticketId,
          materialTypeId,
          orderEntryId,
          materialName: findOverrideNameFromId(materialTypeId, orderEntryId, priceEntries) || materialType.commonName,
          gross: weightAndPrice.gross?.amount || 0,
          tare: weightAndPrice.tare?.amount || 0,
          deductions: weightAndPrice.deductions || [],
          net: weightAndPrice?.netWeight?.amount || 0,
          price: Dinero({ ...weightAndPrice.netPrice }).toUnit(),
          customerPreferredPrice: Dinero({ ...weightAndPrice.customerPreferredPrice }).toUnit(),
          customerPreferredUm: weightAndPrice.customerPreferredUm,
          um: weightAndPrice.um,
        }
      }
    })

    // only formats materials for OBTs (IBTs do not have materialWeights)
    materialWeights.forEach(material => {
      const { gross, tare, weight, materialTypeId, orderEntryId } = material
      formattedMaterials[uuidv4()] = {
        ticketId,
        materialTypeId,
        orderEntryId,
        materialName:
          findOverrideNameFromId(materialTypeId, orderEntryId, priceSheet?.entriesObj) ||
          (defaultPriceSheet?.entriesObj || []).find(entry => entry.materialTypeId === materialTypeId).materialName,
        gross: gross || 0,
        tare: tare || 0,
        deductions: [],
        net: weight || 0,
        price: null,
      }
    })
  })

  return formattedMaterials
}

const getMaterialPriceInfo = ({ material, priceEntries, defaultPriceSheet, defaultPriceType, defaultYardUm }) => {
  const {
    gross,
    tare,
    materialTypeId,
    orderEntryId,
    price,
    net,
    customerPreferredPrice,
    um: materialUm,
    customerPreferredUm,
  } = material

  const priceEntry = priceEntries?.find(
    entry => entry.materialTypeId === materialTypeId && (orderEntryId ? entry.id === orderEntryId : true),
  )
  const priceEntryPrice = priceEntry?.[defaultPriceType]?.price
    ? Dinero({ ...priceEntry?.[defaultPriceType]?.price }).toUnit()
    : null

  const defaultPriceEntry = (defaultPriceSheet?.entriesObj || []).find(entry => entry.materialTypeId === materialTypeId)
  let defaultPriceEntryPrice = null

  // On the outbound flow, the default price is not used. Anything not on contract gets a price of zero
  if (defaultPriceType !== 'sellPrice') {
    defaultPriceEntryPrice = defaultPriceEntry?.[defaultPriceType]?.price
      ? Dinero({ ...defaultPriceEntry?.[defaultPriceType]?.price }).toUnit()
      : null
  }

  const um = materialUm || priceEntry?.weightUnits || defaultPriceEntry?.weightUnits || defaultYardUm
  const materialPrice =
    parseFloat([price, priceEntryPrice, defaultPriceEntryPrice].find(value => value || value === 0)) || 0

  const materialRawValue = net * (materialPrice || 0)
  const materialTotal = roundTo(
    getNetValue({
      gross,
      tare,
      deductions: material.deductions,
      price: materialPrice,
      fromUnit: defaultYardUm,
      toUnit: um,
    }),
    ROUNDING_PRECISIONS.COST,
  )

  return {
    um,
    displayUnit: customerPreferredUm || um,
    displayPrice: customerPreferredPrice || materialPrice,
    materialRawValue,
    materialTotal,
  }
}

const reduceDeductions = ({ material, materialRawValue, um, defaultYardUm }) => {
  const { gross, tare, deductions } = material
  const deductionTypes = { totalWeightDeduction: 0, totalPriceDeduction: 0 }
  const totalDeductions = deductions?.reduce(
    (aggregateDeductions, deduction) => ({
      totalWeightDeduction:
        aggregateDeductions.totalWeightDeduction + getTotalWeightDeduction([deduction], gross - tare),
      totalPriceDeduction:
        aggregateDeductions.totalPriceDeduction +
        getTotalPriceDeduction([deduction], convertUM(materialRawValue, defaultYardUm, um)),
    }),
    deductionTypes,
  )
  return totalDeductions
}

export const aggregateTicketMaterials = ({
  tickets,
  priceSheet,
  priceEntriesByPurchaseOrderId = {},
  isSummary,
  defaultPriceSheet,
  defaultPriceType,
  consumerWeights,
  pricePrepend = '$',
  disableUmDropdowns,
  defaultYardUm,
}) => {
  const totalData = {
    gross: 0,
    tare: 0,
    net: 0,
    total: 0,
  }
  const ticketsById = keyBy(tickets, 'id')
  const formattedMaterials = formatMaterials(tickets, defaultPriceSheet, priceSheet, priceEntriesByPurchaseOrderId)
  const aggregatedMaterials = Object.values(formattedMaterials).reduce((aggregate, material) => {
    const newAggregate = { ...aggregate }
    const { materialTypeId, orderEntryId, materialName, gross, tare, net, deductions, ticketId } = material
    const purchaseOrderId = ticketsById[material.ticketId]?.transportationInfo?.purchaseOrderId
    const priceEntries = priceEntriesByPurchaseOrderId?.[purchaseOrderId] || priceSheet?.entriesObj || []
    const { um, displayUnit, displayPrice, materialRawValue, materialTotal } = getMaterialPriceInfo({
      material,
      priceEntries,
      defaultPriceSheet,
      defaultPriceType,
      defaultYardUm,
    })
    const aggregateKey = `${materialTypeId}${orderEntryId ? `-${orderEntryId}` : ''}-${displayPrice}/${displayUnit}`
    if (!newAggregate[aggregateKey]) {
      newAggregate[aggregateKey] = {
        materialName,
        materialTypeId,
        orderEntryId,
        gross: 0,
        tare: 0,
        totalWeightDeduction: 0,
        totalPriceDeduction: 0,
        deductions,
        net: 0,
        price: displayPrice,
        displayUnit,
        totalValue: 0,
        ticketId,
      }
    }
    const { totalWeightDeduction, totalPriceDeduction } = reduceDeductions({
      material,
      materialRawValue,
      um,
      defaultYardUm,
    })

    newAggregate[aggregateKey].gross = roundTo(newAggregate[aggregateKey].gross + gross, ROUNDING_PRECISIONS.WEIGHT)
    newAggregate[aggregateKey].tare = roundTo(newAggregate[aggregateKey].tare + tare, ROUNDING_PRECISIONS.WEIGHT)
    newAggregate[aggregateKey].totalWeightDeduction = roundTo(
      newAggregate[aggregateKey].totalWeightDeduction + totalWeightDeduction,
      ROUNDING_PRECISIONS.WEIGHT,
    )
    newAggregate[aggregateKey].totalPriceDeduction = roundTo(
      newAggregate[aggregateKey].totalPriceDeduction + totalPriceDeduction,
      ROUNDING_PRECISIONS.COST,
    )
    newAggregate[aggregateKey].net = roundTo(newAggregate[aggregateKey].net + net, ROUNDING_PRECISIONS.WEIGHT)
    newAggregate[aggregateKey].totalValue = roundTo(
      newAggregate[aggregateKey].totalValue + materialTotal,
      ROUNDING_PRECISIONS.COST,
    )

    return newAggregate
  }, {})

  const materialRows = Object.entries(aggregatedMaterials).map(([key, material]) => {
    const {
      materialName,
      materialTypeId,
      orderEntryId,
      gross,
      tare,
      totalWeightDeduction,
      totalPriceDeduction,
      deductions,
      net,
      price,
      displayUnit,
      totalValue,
      ticketId,
    } = material

    const consumerWeightObject =
      consumerWeights?.find(
        entry =>
          entry.outboundTicketId === ticketId &&
          entry.materialTypeId === materialTypeId &&
          (orderEntryId ? entry.orderEntryId === orderEntryId : true),
      ) || {}
    const {
      materialNameOverride,
      consumerGross,
      consumerTare,
      consumerNetWeight,
      consumerPrice,
      um: consumerUm,
    } = consumerWeightObject
    const finalGross = consumerGross || consumerGross === 0 ? consumerGross : gross
    const finalTare = consumerTare || consumerTare === 0 ? consumerTare : tare
    const finalNet = consumerNetWeight || consumerNetWeight === 0 ? consumerNetWeight : net
    const finalPrice = consumerPrice || consumerPrice === 0 ? consumerPrice : price
    const finalUM = disableUmDropdowns ? defaultYardUm : consumerUm || displayUnit
    const finalTotal =
      consumerNetWeight || consumerNetWeight === 0
        ? roundTo(
            getNetValue({
              gross: consumerGross,
              tare: consumerTare,
              deductions: [],
              price: finalPrice,
              fromUnit: defaultYardUm,
              toUnit: consumerUm,
            }),
            ROUNDING_PRECISIONS.COST,
          )
        : totalValue

    totalData.gross = roundTo(totalData.gross + finalGross, ROUNDING_PRECISIONS.WEIGHT)
    totalData.tare = roundTo(totalData.tare + finalTare, ROUNDING_PRECISIONS.WEIGHT)
    totalData.net = roundTo(totalData.net + finalNet, ROUNDING_PRECISIONS.WEIGHT)
    totalData.total = roundTo(totalData.total + finalTotal, ROUNDING_PRECISIONS.COST)

    return {
      id: key,
      materialName: materialNameOverride || materialName,
      gross: formatNumber(finalGross),
      tare: formatNumber(finalTare),
      deductions: generateAdjustmentText(totalWeightDeduction, totalPriceDeduction, pricePrepend),
      ...(isSummary
        ? null
        : {
            deductionNotes:
              deductions?.map(deduction => {
                return deduction.notes
              }) || [],
          }),
      net: formatNumber(finalNet),
      price: `${pricePrepend}${setDecimalPrecision(finalPrice)}/${finalUM}`,
      total: `${pricePrepend}${setDecimalPrecision(finalTotal, { decimalPrecisionOverride: 2 })}`,
    }
  })

  return {
    materials: materialRows,
    totalData: {
      gross: totalData.gross,
      tare: totalData.tare,
      net: totalData.net,
      total: setDecimalPrecision(totalData.total, { decimalPrecisionOverride: 2, shouldFormatNumber: false }),
    },
  }
}
